/**
 * Selection outline (SuperSplat "Selection Outline"): a silhouette drawn around
 * the selected splats.
 *
 * A mask camera that follows the editor camera renders only the selected
 * splats (edit shader mask mode, displayFlags.x = 3) into an offscreen target;
 * a full-screen pass on the overlay layer then outlines the mask's outer edge.
 * "Inside" is the mask's coverage averaged over a small neighbourhood, so the
 * pinholes and speckles of a sparse scan don't each get a ring of their own:
 * the outline follows the selected region as the selection tint would show it.
 * Nothing is created until the outline is switched on.
 */
import * as pc from 'playcanvas';

const MASK_LAYER = 'SplatEditMask';

const quadVS = /* glsl */`
attribute vec2 aPosition;
varying vec2 vUv;
void main(void) {
    gl_Position = vec4(aPosition, 0.0, 1.0);
    vUv = aPosition * 0.5 + 0.5;
}
`;

const outlineFS = /* glsl */`
uniform sampler2D maskTexture;
uniform vec4 outlineColor;
uniform vec2 maskTexel;
uniform float outlineWidth;
uniform float coverageStep;
varying vec2 vUv;
float inside(vec2 uv) {
    float sum = 0.0;
    for (int x = -1; x <= 1; x++) {
        for (int y = -1; y <= 1; y++) {
            sum += texture2D(maskTexture, uv + vec2(float(x), float(y)) * maskTexel * coverageStep).a;
        }
    }
    return sum > 9.0 * 0.2 ? 1.0 : 0.0;
}
void main(void) {
    float c = inside(vUv);
    float n = 0.0;
    for (int i = 0; i < 8; i++) {
        float a = float(i) * 0.785398;
        n = max(n, inside(vUv + vec2(cos(a), sin(a)) * maskTexel * outlineWidth));
    }
    if (c > 0.5 || n < 0.5) discard;
    gl_FragColor = vec4(outlineColor.rgb, 1.0) * outlineColor.a;
}
`;

const quadVSW = /* wgsl */`
attribute aPosition: vec2f;
varying vUv: vec2f;
@vertex
fn vertexMain(input: VertexInput) -> VertexOutput {
    var output: VertexOutput;
    output.position = vec4f(input.aPosition, 0.0, 1.0);
    // WebGPU textures are top-down
    output.vUv = vec2f(input.aPosition.x * 0.5 + 0.5, 0.5 - input.aPosition.y * 0.5);
    return output;
}
`;

const outlineFSW = /* wgsl */`
var maskTexture: texture_2d<f32>;
var maskTextureSampler: sampler;
uniform outlineColor: vec4f;
uniform maskTexel: vec2f;
uniform outlineWidth: f32;
uniform coverageStep: f32;
varying vUv: vec2f;
fn inside(uv: vec2f) -> f32 {
    var sum: f32 = 0.0;
    for (var x: i32 = -1; x <= 1; x++) {
        for (var y: i32 = -1; y <= 1; y++) {
            sum += textureSampleLevel(maskTexture, maskTextureSampler, uv + vec2f(f32(x), f32(y)) * uniform.maskTexel * uniform.coverageStep, 0.0).a;
        }
    }
    return select(0.0, 1.0, sum > 9.0 * 0.2);
}
@fragment
fn fragmentMain(input: FragmentInput) -> FragmentOutput {
    var output: FragmentOutput;
    let c = inside(input.vUv);
    var n: f32 = 0.0;
    for (var i: i32 = 0; i < 8; i++) {
        let a = f32(i) * 0.785398;
        n = max(n, inside(input.vUv + vec2f(cos(a), sin(a)) * uniform.maskTexel * uniform.outlineWidth));
    }
    if (c > 0.5 || n < 0.5) {
        discard;
        return output;
    }
    output.color = vec4f(uniform.outlineColor.rgb, 1.0) * uniform.outlineColor.a;
    return output;
}
`;

export class SelectionOutline {
    private readonly app: pc.AppBase;
    private readonly mainCamera: pc.CameraComponent;
    readonly layer: pc.Layer;
    private readonly overlayLayer: pc.Layer;
    private readonly maskCameraEntity: pc.Entity;
    private texture: pc.Texture;
    private target: pc.RenderTarget;
    private readonly quad: pc.Entity;
    private readonly material: pc.ShaderMaterial;
    private readonly onUpdate: () => void;
    color: [number, number, number] = [1, 1, 0];
    width = 2;
    /** Spacing of the coverage samples, in CSS pixels: gaps narrower than ~3x this are bridged. */
    coverageStep = 3;

    constructor(app: pc.AppBase, mainCamera: pc.CameraComponent, overlayLayer: pc.Layer) {
        this.app = app;
        this.mainCamera = mainCamera;
        this.overlayLayer = overlayLayer;
        const layers = app.scene.layers;
        this.layer = layers.getLayerByName(MASK_LAYER) ?? new pc.Layer({ name: MASK_LAYER });
        if (!layers.getLayerByName(MASK_LAYER)) layers.push(this.layer);

        const { texture, target } = this._createTarget();
        this.texture = texture;
        this.target = target;

        this.maskCameraEntity = new pc.Entity('splat-edit-mask-camera');
        this.maskCameraEntity.addComponent('camera', {
            layers: [this.layer.id],
            clearColor: new pc.Color(0, 0, 0, 0),
            renderTarget: this.target,
            priority: (mainCamera.priority ?? 0) - 1,
        } as never);
        mainCamera.entity.addChild(this.maskCameraEntity);

        this.material = new pc.ShaderMaterial({
            uniqueName: 'SplatEditSelectionOutline',
            vertexGLSL: quadVS,
            fragmentGLSL: outlineFS,
            vertexWGSL: quadVSW,
            fragmentWGSL: outlineFSW,
            attributes: { aPosition: pc.SEMANTIC_POSITION },
        } as never);
        this.material.blendType = pc.BLEND_PREMULTIPLIED;
        this.material.depthTest = false;
        this.material.depthWrite = false;
        this.material.cull = pc.CULLFACE_NONE;
        this.material.update();

        const mesh = new pc.Mesh(app.graphicsDevice);
        mesh.setPositions(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3);
        mesh.setIndices([0, 1, 2]);
        mesh.update(pc.PRIMITIVE_TRIANGLES);
        const mi = new pc.MeshInstance(mesh, this.material);
        mi.cull = false;
        this.quad = new pc.Entity('splat-edit-outline-quad');
        this.quad.addComponent('render', { meshInstances: [mi], layers: [overlayLayer.id], castShadows: false, receiveShadows: false } as never);
        app.root.addChild(this.quad);

        this.onUpdate = () => this._sync();
        app.on('prerender', this.onUpdate);
        this._sync();
    }

    destroy(): void {
        this.app.off('prerender', this.onUpdate);
        this.quad.destroy();
        this.maskCameraEntity.destroy();
        this.target.destroy();
        this.texture.destroy();
        this.material.destroy();
        this.app.scene.layers.remove(this.layer);
    }

    private _createTarget(): { texture: pc.Texture; target: pc.RenderTarget } {
        const device = this.app.graphicsDevice;
        const texture = new pc.Texture(device, {
            name: 'splatEditSelectionMask',
            width: Math.max(1, device.width), height: Math.max(1, device.height),
            format: pc.PIXELFORMAT_RGBA8, mipmaps: false,
            minFilter: pc.FILTER_LINEAR, magFilter: pc.FILTER_LINEAR,
            addressU: pc.ADDRESS_CLAMP_TO_EDGE, addressV: pc.ADDRESS_CLAMP_TO_EDGE,
        });
        const target = new pc.RenderTarget({ colorBuffer: texture, depth: false });
        return { texture, target };
    }

    private _sync(): void {
        const device = this.app.graphicsDevice;
        if (this.texture.width !== device.width || this.texture.height !== device.height) {
            const cam = this.maskCameraEntity.camera!;
            const old = { texture: this.texture, target: this.target };
            const next = this._createTarget();
            this.texture = next.texture;
            this.target = next.target;
            cam.renderTarget = this.target;
            old.target.destroy();
            old.texture.destroy();
        }
        // follow the editor camera's projection
        const src = this.mainCamera;
        const cam = this.maskCameraEntity.camera!;
        cam.projection = src.projection;
        cam.fov = src.fov;
        cam.orthoHeight = src.orthoHeight;
        cam.nearClip = src.nearClip;
        cam.farClip = src.farClip;
        cam.horizontalFov = src.horizontalFov;
        cam.priority = (src.priority ?? 0) - 1;
        this.material.setParameter('maskTexture', this.texture);
        this.material.setParameter('outlineColor', [...this.color, 1]);
        this.material.setParameter('maskTexel', [1 / this.texture.width, 1 / this.texture.height]);
        const dpr = Math.max(1, device.width / Math.max(1, (device.canvas as HTMLCanvasElement).clientWidth || device.width));
        this.material.setParameter('outlineWidth', this.width * dpr);
        this.material.setParameter('coverageStep', this.coverageStep * dpr);
    }
}
